import { useState } from "react";
import { ArrowRightIcon } from "lucide-react";

import { DashboardKpiCardHeader } from "@/components/dashboard/dashboard-stats/DashboardKpiCardHeader";
import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetDashboardStafferFunnelStats } from "@/hooks/queries/useGetDashboardStafferFunnelStats";
import {
  ALL_USERS_KEY,
  DASHBOARD_STAT_CARD_HEIGHT_CLASS,
  performanceTierGradientClass,
  performanceTierTextClass,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import {
  DashboardSelectedUser,
  DashboardStafferFunnelStats,
  PerformanceTier,
} from "@/types";

enum FunnelRateKind {
  SCREEN_TO_SUBMIT = "SCREEN_TO_SUBMIT",
  SUBMIT_TO_INTERVIEW = "SUBMIT_TO_INTERVIEW",
  INTERVIEW_TO_OFFER = "INTERVIEW_TO_OFFER",
  OFFER_TO_PLACEMENT = "OFFER_TO_PLACEMENT",
}

// A 1:1 conversion is the best possible rate and anchors the bottom of the
// plot; the stage goal sits exactly on the mid-card goal line.
const MIN_RATIO = 1;
const PLOT_TOP_PERCENT = 15;
const PLOT_BOTTOM_PERCENT = 76;
const GOAL_LINE_PERCENT = 50;

// Staggered lanes so avatars plotted in the same column rarely overlap.
const HORIZONTAL_POSITIONS = [24, 50, 74, 36, 66, 84];

// Ring/fill/text combo for a scatter avatar, softer than the shared text tier.
const tierAvatarClass: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]:
    "bg-[#DEF3E8] text-[#1D7A42] ring-[#BDE7CE] dark:bg-[#315F34] dark:text-[#DCEBDA] dark:ring-[#39753B]",
  [PerformanceTier.ORANGE]:
    "bg-[#FBECDD] text-[#A85818] ring-[#F8D8BC] dark:bg-[#8D4721] dark:text-[#FFC9AB] dark:ring-[#F39562]",
  [PerformanceTier.RED]:
    "bg-[#F9E3E3] text-[#9B3030] ring-[#F1C6C6] dark:bg-[#602A24] dark:text-[#E2C4BC] dark:ring-[#783129]",
};

// Per-rate config: the funnel stage pair it converts between, the team's goal
// rate, and where the orange band ends. A staffer with zero of either stage
// has no rate and is unranked.
const RATE_CONFIGS: {
  kind: FunnelRateKind;
  label: string;
  shortLabel: string;
  goal: number;
  orangeMax: number;
  getFrom: (stats: DashboardStafferFunnelStats) => number;
  getTo: (stats: DashboardStafferFunnelStats) => number;
}[] = [
  {
    kind: FunnelRateKind.SCREEN_TO_SUBMIT,
    label: "Screen to Submit",
    shortLabel: "Submit",
    goal: 2.2,
    orangeMax: 2.9,
    getFrom: (stats) => stats.screens,
    getTo: (stats) => stats.submits,
  },
  {
    kind: FunnelRateKind.SUBMIT_TO_INTERVIEW,
    label: "Submit to Interview",
    shortLabel: "Interview",
    goal: 2,
    orangeMax: 2.5,
    getFrom: (stats) => stats.submits,
    getTo: (stats) => stats.interviews,
  },
  {
    kind: FunnelRateKind.INTERVIEW_TO_OFFER,
    label: "Interview to Offer",
    shortLabel: "Offer",
    goal: 2.3,
    orangeMax: 3,
    getFrom: (stats) => stats.interviews,
    getTo: (stats) => stats.offers,
  },
  {
    kind: FunnelRateKind.OFFER_TO_PLACEMENT,
    label: "Offer to Placement",
    shortLabel: "Placement",
    goal: 1.1,
    // Orange down to a 75% conversion; below that reads as red.
    orangeMax: 1 / 0.75,
    getFrom: (stats) => stats.offers,
    getTo: (stats) => stats.placements,
  },
];

// How many of the "from" stage it takes to produce one of the "to" stage
// (21 submits and 10 interviews → 2.1), or null when either count is zero.
const rateForStat = (
  config: (typeof RATE_CONFIGS)[number],
  stats: DashboardStafferFunnelStats,
): number | null => {
  const from = config.getFrom(stats);
  const to = config.getTo(stats);
  if (from === 0 || to === 0) return null;
  return Math.round((from / to) * 10) / 10;
};

const formatRatio = (rate: number): string =>
  Number.isInteger(rate) ? rate.toFixed(0) : rate.toFixed(1);

// Staffers sorted best-first for a rate (fewer inputs per conversion wins);
// rateless staffers sink to the bottom.
const sortForRate = (
  allStats: DashboardStafferFunnelStats[],
  config: (typeof RATE_CONFIGS)[number],
): DashboardStafferFunnelStats[] =>
  [...allStats].sort((a, b) => {
    const aRate = rateForStat(config, a);
    const bRate = rateForStat(config, b);
    if ((aRate === null) !== (bRate === null)) return aRate === null ? 1 : -1;
    return (aRate ?? 0) - (bRate ?? 0);
  });

const tierForRate = (
  config: (typeof RATE_CONFIGS)[number],
  rate: number,
): PerformanceTier => {
  if (rate <= config.goal) return PerformanceTier.GREEN;
  if (rate <= config.orangeMax) return PerformanceTier.ORANGE;
  return PerformanceTier.RED;
};

const verticalPositionPercent = (
  rate: number,
  config: (typeof RATE_CONFIGS)[number],
  maxRate: number,
): number => {
  const clamped = Math.max(rate, MIN_RATIO);
  if (clamped <= config.goal) {
    return (
      PLOT_BOTTOM_PERCENT -
      ((clamped - MIN_RATIO) / (config.goal - MIN_RATIO)) *
        (PLOT_BOTTOM_PERCENT - GOAL_LINE_PERCENT)
    );
  }
  return (
    GOAL_LINE_PERCENT -
    ((clamped - config.goal) / (maxRate - config.goal)) *
      (GOAL_LINE_PERCENT - PLOT_TOP_PERCENT)
  );
};

const FunnelStageLabel = ({ shortLabel }: { shortLabel: string }) => {
  /* Render */
  const render = () => (
    <Row className="pointer-events-none absolute inset-x-2 bottom-1.5 z-30 items-center justify-start gap-0.5">
      <ArrowRightIcon className="h-3 w-3 shrink-0 text-slate-500" />
      <Text className="truncate font-plex-mono text-[9px] font-medium uppercase tracking-wider text-slate-700">
        {shortLabel}
      </Text>
    </Row>
  );

  return render();
};

const FunnelAvatar = ({
  goal,
  horizontalPosition,
  name,
  onHover,
  rate,
  tier,
  verticalPosition,
}: {
  goal: number;
  horizontalPosition: number;
  name: string;
  onHover: () => void;
  rate: number;
  tier: PerformanceTier;
  verticalPosition: number;
}) => {
  const [firstName, ...restNames] = name.split(" ");
  const initials = formatting.initials(firstName || "", restNames.join(" "));

  /* Render */
  const render = () => (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <Row
          className={cn(
            "absolute z-20 h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-default items-center justify-center rounded-full border border-white shadow-[0_1px_3px_rgba(15,23,42,0.28)] ring-1 transition-transform hover:z-30 hover:scale-110",
            tierAvatarClass[tier],
          )}
          style={{
            left: `${horizontalPosition.toString()}%`,
            top: `${verticalPosition.toString()}%`,
          }}
          onMouseEnter={onHover}
        >
          <Text className="text-[10px] font-medium leading-none text-current">
            {initials}
          </Text>
        </Row>
      </TooltipTrigger>
      <TooltipContent className="pointer-events-none px-3 py-2" side="top">
        <Text className="text-xs font-semibold text-slate-900">{name}</Text>
        <Text
          className={cn(
            "text-lg font-semibold tabular-nums leading-tight",
            performanceTierTextClass[tier],
          )}
        >
          {formatRatio(rate)}:1
        </Text>
        <Text className="text-[10px] font-normal text-slate-500">
          Goal {formatRatio(goal)}:1
        </Text>
      </TooltipContent>
    </Tooltip>
  );

  return render();
};

// One All-view stage column: every rated staffer plotted as an avatar over a
// green goal zone whose top edge is the stage's goal rate.
const FunnelSection = ({
  allStats,
  config,
  index,
}: {
  allStats: DashboardStafferFunnelStats[];
  config: (typeof RATE_CONFIGS)[number];
  index: number;
}) => {
  /* React Hooks */
  const [isGoalTooltipOpen, setIsGoalTooltipOpen] = useState(false);

  /* Helpers */
  const plotted = sortForRate(allStats, config)
    .map((stats) => ({ stats, rate: rateForStat(config, stats) }))
    .filter(
      (entry): entry is { stats: DashboardStafferFunnelStats; rate: number } =>
        entry.rate !== null,
    );
  const maxRate = Math.max(...plotted.map((entry) => entry.rate), MIN_RATIO);

  /* Handlers */
  const handleAvatarHover = () => {
    setIsGoalTooltipOpen(false);
  };

  /* Render */
  const render = () => (
    <Column
      className={cn(
        "relative min-w-0 overflow-visible",
        index > 0 && "border-l border-solid border-slate-200/70",
      )}
    >
      <Column
        className="performance-gradient-green pointer-events-none absolute inset-x-0 bottom-0"
        style={{
          borderTopColor: "rgb(226 232 240 / 70%)",
          top: `${GOAL_LINE_PERCENT.toString()}%`,
        }}
      />
      <Tooltip open={isGoalTooltipOpen} onOpenChange={setIsGoalTooltipOpen}>
        <TooltipTrigger asChild>
          <Column
            className="performance-gradient-green-hover absolute inset-x-0 bottom-0 z-10 cursor-default transition-colors duration-150"
            style={{ top: `${GOAL_LINE_PERCENT.toString()}%` }}
          />
        </TooltipTrigger>
        <TooltipContent className="px-3 py-2" side="bottom">
          <Text className="text-xs font-medium text-slate-700">
            {config.label}
          </Text>
          <Text
            className={cn(
              "text-lg font-semibold tabular-nums leading-tight",
              performanceTierTextClass[PerformanceTier.GREEN],
            )}
          >
            {formatRatio(config.goal)}:1
          </Text>
          <Text className="text-[10px] font-normal text-slate-500">
            Conversion rate goal
          </Text>
        </TooltipContent>
      </Tooltip>

      {plotted.map((entry, plottedIndex) => (
        <FunnelAvatar
          key={entry.stats.userId}
          goal={config.goal}
          horizontalPosition={
            HORIZONTAL_POSITIONS[plottedIndex % HORIZONTAL_POSITIONS.length]
          }
          name={entry.stats.name}
          onHover={handleAvatarHover}
          rate={entry.rate}
          tier={tierForRate(config, entry.rate)}
          verticalPosition={verticalPositionPercent(
            entry.rate,
            config,
            maxRate,
          )}
        />
      ))}

      <FunnelStageLabel shortLabel={config.shortLabel} />
    </Column>
  );

  return render();
};

// One personal-view stage column: a gradient band rising to where the staffer
// sits on the goal-anchored scale, with the rate, goal, and rank on hover.
const SelectedStafferFunnelSection = ({
  config,
  maxRate,
  rank,
  rate,
}: {
  config: (typeof RATE_CONFIGS)[number];
  maxRate: number;
  rank: number | null;
  rate: number | null;
}) => {
  /* Render */
  const renderRank = (rankValue: number) => {
    if (rankValue === 1) return "1st 🥇";
    if (rankValue === 2) return "2nd 🥈";
    if (rankValue === 3) return "3rd 🥉";
    return `${rankValue.toString()}${dates.getDaySuffix(rankValue)}`;
  };

  const render = () => {
    if (rate === null) {
      return (
        <Column className="relative min-w-0">
          <Text className="mt-4 px-2 text-center text-[10px] font-normal italic text-slate-400">
            No stats
          </Text>
          <FunnelStageLabel shortLabel={config.shortLabel} />
        </Column>
      );
    }

    const tier = tierForRate(config, rate);
    const verticalPosition = verticalPositionPercent(rate, config, maxRate);
    return (
      <Column className="relative min-w-0 overflow-hidden">
        <Tooltip disableHoverableContent>
          <TooltipTrigger asChild>
            <Column
              className={cn(
                "absolute inset-x-0 bottom-0 z-30 cursor-default rounded-t",
                performanceTierGradientClass[tier],
              )}
              style={{ top: `${verticalPosition.toString()}%` }}
            />
          </TooltipTrigger>
          <TooltipContent className="px-3 py-2" side="top" sideOffset={4}>
            <Text className="text-xs font-medium text-slate-700">
              {config.label}
            </Text>
            <Row className="items-baseline gap-1">
              <Text
                className={cn(
                  "text-lg font-semibold tabular-nums leading-tight",
                  performanceTierTextClass[tier],
                )}
              >
                {formatRatio(rate)}:1
              </Text>
              <Text className="text-[10px] font-normal text-slate-500">
                Goal {formatRatio(config.goal)}:1
              </Text>
            </Row>
            {rank !== null ? (
              <Text className="text-[10px] font-normal text-slate-500">
                {renderRank(rank)}
              </Text>
            ) : null}
          </TooltipContent>
        </Tooltip>
        <FunnelStageLabel shortLabel={config.shortLabel} />
      </Column>
    );
  };

  return render();
};

// 45-day funnel conversion ratios: a per-stage scatter of every staffer in
// the All view, or the selected staffer's rates and ranks on the same scale.
export const DashboardStafferFunnelRatesCard = ({
  eyebrow,
  selectedUserId,
  title,
  // Rest of the stat row so all the cards reveal together.
  isPending: isRowPending,
  onNext,
}: {
  eyebrow: string;
  selectedUserId: DashboardSelectedUser;
  title: string;
  isPending: boolean;
  onNext: () => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: allStats, isPending: isFetchPending } =
    useGetDashboardStafferFunnelStats();
  const isPending = isRowPending || isFetchPending;

  /* Helpers */
  const isAllView = selectedUserId === ALL_USERS_KEY;

  const rankForRate = (
    config: (typeof RATE_CONFIGS)[number],
    userId: number,
  ): number | null => {
    const ranked = sortForRate(allStats ?? [], config).filter(
      (stats) => rateForStat(config, stats) !== null,
    );
    const index = ranked.findIndex((stats) => stats.userId === userId);
    return index === -1 ? null : index + 1;
  };

  const maxRateForConfig = (config: (typeof RATE_CONFIGS)[number]): number =>
    Math.max(
      ...(allStats ?? []).map((stats) => rateForStat(config, stats) ?? 0),
      MIN_RATIO,
    );

  /* Render */
  const renderPending = () => (
    <Column className="min-h-0 flex-1 gap-2">
      <Skeleton className="h-full w-full" />
    </Column>
  );

  const renderAllStaffersChart = () => (
    <TooltipProvider delayDuration={100}>
      <div className="grid min-h-0 flex-1 grid-cols-4">
        {RATE_CONFIGS.map((config, index) => (
          <FunnelSection
            key={config.kind}
            allStats={allStats ?? []}
            config={config}
            index={index}
          />
        ))}
      </div>
    </TooltipProvider>
  );

  const renderSelectedStafferChart = () => {
    const stats = (allStats ?? []).find((row) => row.userId === selectedUserId);
    return (
      <TooltipProvider delayDuration={100}>
        <div className="relative grid min-h-0 flex-1 grid-cols-4">
          <Column className="pointer-events-none absolute inset-x-0 top-1/2 z-20 h-px bg-slate-200/70" />
          {RATE_CONFIGS.map((config) => (
            <SelectedStafferFunnelSection
              key={config.kind}
              config={config}
              maxRate={maxRateForConfig(config)}
              rank={stats ? rankForRate(config, stats.userId) : null}
              rate={stats ? rateForStat(config, stats) : null}
            />
          ))}
        </div>
      </TooltipProvider>
    );
  };

  const render = () => (
    <Card
      className={cn(
        "flex min-h-0 w-full min-w-0 flex-col overflow-hidden px-0 pb-0 pt-3.5 shadow-none",
        DASHBOARD_STAT_CARD_HEIGHT_CLASS,
      )}
    >
      <DashboardKpiCardHeader
        className="mb-2 px-4"
        eyebrow={eyebrow}
        onNext={onNext}
        title={title}
      />
      {isPending ? renderPending() : null}
      {!isPending && isAllView ? renderAllStaffersChart() : null}
      {!isPending && !isAllView ? renderSelectedStafferChart() : null}
    </Card>
  );

  return render();
};
